import { defaultLocale, Languages } from 'libs/locale';

import landingSlugs from './landing-slugs.json';

/**
 * SINGLE SOURCE OF TRUTH for landing-page slug localization.
 *
 * English registry key → localized URL slug per language. Kept dependency-free (no React /
 * next/dynamic imports) so the core Link component can import it to build locale-aware hrefs
 * from a canonical key. registry.tsx also consumes this map for slug↔key resolution + hreflang.
 *
 * Refer to a landing page anywhere by its canonical key (e.g. <Link route="kyb-verification" />);
 * the URL for the active locale is then constructed from this map — header, footer, in-page CTAs,
 * everywhere, with no per-call-site work.
 */
export const LANDING_SLUGS = landingSlugs as Record<string, Partial<Record<Languages, string>>>;

/** Canonical key → localized slug for `lang` (falls back to the key itself for defaultLocale / unknown keys). */
export const localizedSlug = (route: string, lang: string): string =>
  (lang !== defaultLocale && LANDING_SLUGS[route]?.[lang as Languages]) || route;

/** Whether a landing key has a distinct localized slug in this language. */
export const hasLocalizedSlug = (key: string, lang: string): boolean =>
  lang !== defaultLocale && Boolean(LANDING_SLUGS[key]?.[lang as Languages]);

// `${lang}:${urlSlug}` → canonical registry key. Built once at module load.
const slugToKey: Record<string, string> = {};
for (const [key, slugs] of Object.entries(LANDING_SLUGS)) {
  slugToKey[`${defaultLocale}:${key}`] = key;
  for (const [lang, slug] of Object.entries(slugs)) {
    if (slug) {
      slugToKey[`${lang}:${slug}`] = key;
    }
  }
}

/** Resolve a requested (lang, urlSlug — localized slug or English key) to its canonical key, else undefined. */
export const keyForLocalizedSlug = (lang: string, urlSlug: string): string | undefined =>
  slugToKey[`${lang}:${urlSlug}`];
